import React from 'react';
import { useSelector } from 'react-redux';

import { useBi, useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { groupFeedTopicsSelectTopicInFeed } from '@wix/bi-logger-groups/v2';
import { useCurrentStateAndParams } from '@wix/tpa-router/react';

import {
  selectAllTopics,
  selectFeedPermissionsByGroup,
  selectTopicsState,
} from 'store/selectors';
import { EFilterKeys } from 'api/feed/constants';

import { useDidUpdate } from 'common/hooks';
import { useController } from 'common/context/controller';
import { TopicLink } from 'common/components/TopicLink';

import { TextButton } from 'wui/TextButton';
import { Card } from 'wui/Card';
import { CardHeader } from 'wui/CardHeader';
import { CardContent } from 'wui/CardContent';
import { CardActions } from 'wui/CardActions';
import { List } from 'wui/List';
import { Show } from 'wui/Show';
import { Hide } from 'wui/Hide';
import { Skeleton } from 'wui/Skeleton';

export interface IGroupTopicsProps {
  groupId: string;
}

export function GroupTopics(props: IGroupTopicsProps) {
  const { groupId } = props;

  const { t } = useTranslation();
  const { topics$ } = useController();
  const { isMobile } = useEnvironment();
  const { params } = useCurrentStateAndParams();

  const topics = useSelector(selectAllTopics);
  const topicState = useSelector(selectTopicsState);
  const permissions = useSelector(selectFeedPermissionsByGroup(groupId));

  const [collapsed, setCollapsed] = React.useState(!isMobile);
  const activeId = params[EFilterKeys.TOPICS];
  const isLoading = topicState.statuses.fetch.pending;

  const bi = useBi();

  useDidUpdate(() => {
    if (permissions.canViewPosts) {
      topics$.fetch(groupId);
    }
  }, [permissions.canViewPosts]);

  if (!topics.length || !permissions.canViewPosts) {
    return null;
  }

  const topicsToShow = collapsed ? topics.slice(0, 10) : topics;

  return (
    <Card sideBorders={!isMobile}>
      <CardHeader
        wired
        title={t('groups-web.discussion.topics.widget.title')}
      />
      <CardContent>
        <List
          disablePadding
          wrap={!isMobile}
          direction="horizontal"
          gap={isMobile ? 'SP2' : 'SP1'}
          scrollable={isMobile}
        >
          <Show if={isLoading}>
            <Skeleton variant="rect" width={90} height={22} />
            <Skeleton variant="rect" width={80} height={22} />
            <Skeleton variant="rect" width={90} height={22} />
            <Skeleton variant="rect" width={110} height={22} />
          </Show>
          <Hide if={isLoading}>
            {topicsToShow.map((topic) => (
              <li key={topic.id} style={{ maxWidth: '100%' }}>
                <TopicLink
                  wired
                  withCounter
                  groupId={groupId}
                  topic={topic}
                  onClick={handleTopicClick}
                  isRemovable={topic.id === activeId}
                  active={topic.id === activeId}
                  ellipsis
                />
              </li>
            ))}
          </Hide>
        </List>
      </CardContent>
      <Show if={!isMobile && topics.length > 10}>
        <CardActions>
          <TextButton wired onClick={toggleCollapsed}>
            {collapsed
              ? t('groups-web.discussion.topics.widget.more')
              : t('groups-web.discussion.topics.widget.less')}
          </TextButton>
        </CardActions>
      </Show>
    </Card>
  );

  function toggleCollapsed() {
    setCollapsed(!collapsed);
  }

  function handleTopicClick() {
    bi.report(
      groupFeedTopicsSelectTopicInFeed({
        groupId: groupId as string,
        origin: 'feed_topics_section',
      }),
    );
  }
}

GroupTopics.displayName = 'GroupTopics';
